import { Link } from "react-router-dom";
import LegalLayout from "./LegalLayout";

const THIRD_PARTY = [
    ["Apple", "https://www.apple.com/legal/privacy/"],
    ["Google", "https://policies.google.com/privacy"],
    ["OpenAI", "https://openai.com/policies/privacy-policy"],
    ["xAI", "https://x.ai/legal/privacy-policy"],
    ["RevenueCat", "https://www.revenuecat.com/privacy"],
];

export default function PrivacyPolicy() {
    return (
        <LegalLayout
            title="Privacy Policy"
            lastUpdated="October 20, 2026"
            testId="page-privacy-policy"
            seoTitle="Privacy Policy | TRT Plus"
            seoDescription="How TRT Plus handles your data. No accounts, no tracking. Your protocol, bloodwork and health data stay on your device."
            seoPath="/privacy-policy"
        >
            <h2>Your data stays on your device</h2>
            <p>
                TRT Plus has no accounts and does not track you. Your
                protocol, Protocol Log, bloodwork, check-ins, notes and
                settings are stored only on your device. We do not sell,
                rent or share your data, and we do not use it for
                advertising.
            </p>

            <h2>AI processing</h2>
            <p>
                Some features send content to third-party AI services, only
                when you use them. We do not keep a copy.
            </p>
            <p>
                OpenAI reads the files you upload to extract values: lab
                reports (photos, scans, PDFs, spreadsheets) and protocol
                history imports (spreadsheets, documents, photos or pasted
                text). These files may contain personal details such as your
                name or date of birth, and any notes you include.
            </p>
            <p>
                xAI generates your AI bloodwork report from your biomarker
                values, protocol, blood test timing and body measurements.
                It does not receive images.
            </p>
            <p>
                Under their API terms, these providers may keep data for a
                limited period, typically up to 30 days, for abuse
                monitoring. They do not use it to train their models.
            </p>

            <h2>Apple Health and Health Connect</h2>
            <p>
                If you connect Apple Health (iPhone) or Health Connect
                (Android), TRT Plus reads the data types you allow, such as
                sleep, heart rate, HRV, blood pressure, weight, body
                composition, steps, workouts, blood oxygen and blood
                glucose. On iPhone this can also include Apple Watch
                notifications. TRT Plus only reads this data and never
                writes to Apple Health or Health Connect. With your
                permission, it can also read your past history to build your
                Recovery baseline and fill in your Protocol Log.
            </p>
            <p>
                Health data is stored only in the app on your phone, is not
                included in backups, and is never sold, used for advertising
                or sent to us. It leaves your phone only if you choose to
                share it:
            </p>
            <ul>
                <li>
                    by including it when you use Discuss with your AI, or
                </li>
                <li>
                    by saving Health readings as your own blood pressure or
                    body fat. Those saved values are then treated like any
                    other measurement you enter, including in your AI
                    bloodwork report.
                </li>
            </ul>
            <p>
                TRT Plus's use of information received from Health Connect
                adheres to the{" "}
                <a
                    href="https://support.google.com/googleplay/android-developer/answer/12991134"
                    target="_blank"
                    rel="noreferrer"
                >
                    Health Connect Permissions policy
                </a>
                , including the Limited Use requirements.
            </p>
            <p>
                You can disconnect at any time in the app (Protocol Log ›
                Log Settings), which also deletes the health data the app
                has stored. You can also remove access in iOS Settings ›
                Health › Data Access &amp; Devices › TRT Plus, or on Android
                in Settings › Health Connect › App permissions › TRT Plus.
            </p>

            <h2>Backups</h2>
            <p>
                Export creates a file that you save wherever you choose. We
                have no copy and cannot recover it. The file contains
                sensitive health information, so store it carefully.
            </p>

            <h2>Sharing with your AI</h2>
            <p>
                Discuss with your AI and Share Report send text to the app
                you pick, such as ChatGPT, Gemini or Grok. Nothing passes
                through us, and that app's privacy policy applies.
            </p>

            <h2>Purchases</h2>
            <p>
                In-app purchases are handled by Apple or Google. RevenueCat
                manages your PRO status using an anonymous ID and your
                purchase records.
            </p>

            <h2>Device permissions</h2>
            <p>
                Camera, photo library and files are used only to import
                documents you choose. Notifications are scheduled on your
                device.
            </p>

            <h2>Age</h2>
            <p>
                TRT Plus is intended for adults aged 18 and over. We do not
                knowingly collect data from anyone under 18.
            </p>

            <h2>Your control</h2>
            <p>
                You can view, edit or delete your data in the app at any
                time. To remove everything, see our{" "}
                <Link to="/data-deletion">Data Deletion</Link> page.
            </p>

            <h2>Changes to this policy</h2>
            <p>
                We may update this policy as the App changes. The date at
                the top shows when it was last updated.
            </p>

            <h2>Third-party privacy policies</h2>
            <ul>
                {THIRD_PARTY.map(([name, href]) => (
                    <li key={name}>
                        {name}:{" "}
                        <a href={href} target="_blank" rel="noreferrer">
                            {href}
                        </a>
                    </li>
                ))}
            </ul>
        </LegalLayout>
    );
}
